import React, { useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Stack, Typography,
} from '@mui/material';
import {
  HealthAndSafetyOutlined as CheckIcon, PlayArrow as RunIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * "Is the AI working?", answered on demand.
 *
 * The check asks Gemini to reply with one word, which is a real call on a real
 * account: it costs a fraction of a penny and counts towards the month's spend shown
 * above. So it runs when somebody presses the button and at no other time — never on
 * load, never on a timer, and it is not something to point an uptime monitor at.
 */
export default function AiServiceCheck() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // { ok, status, message, model, preview, at }

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API_URL}/health/gemini`);
      setResult({
        ok: !!data.gemini_ok,
        status: data.status,
        message: data.message,
        model: data.model,
        preview: data.response_preview,
        at: new Date(),
      });
    } catch (err) {
      // A refusal or a network failure is an answer too: the service is not reachable
      // from here, which is what the operator wants to know.
      setResult({
        ok: false,
        status: 'error',
        message: err.response?.data?.detail || err.userMessage || 'Could not reach the server.',
        at: new Date(),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
          <CheckIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="h6" fontWeight={700}>AI service check</Typography>
          {result && (
            <Chip
              size="small"
              color={result.ok ? 'success' : 'error'}
              label={result.ok ? 'Working' : 'Not working'}
            />
          )}
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 620 }}>
          Asks the AI to answer one short question, to confirm the key and the model are
          working. This is a real, billed call — a fraction of a penny, counted in the
          spend above — so it only runs when you press the button.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={run}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
          >
            {busy ? 'Checking…' : result ? 'Check again' : 'Run the check'}
          </Button>
          {result && (
            <Typography variant="caption" color="text.secondary">
              Last checked {result.at.toLocaleTimeString()}
              {result.model ? ` · ${result.model}` : ''}
            </Typography>
          )}
        </Box>

        {result && (
          <Alert
            severity={result.ok ? 'success' : 'error'}
            sx={{ mt: 2, borderRadius: 2 }}
          >
            {result.ok ? (
              <>The AI answered. Note generation and patient summaries should be working.</>
            ) : (
              <>
                <b>The AI did not answer.</b>{' '}
                {result.message || 'Check the API key and the model name, then try again.'}
                {result.status === 'unexpected' && result.preview
                  ? ` It replied: “${result.preview}”.`
                  : ''}
              </>
            )}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
